'use client';

import React, { useState, useEffect } from 'react';
import { AssetDto, assetsApi } from '@/app/api/assets';
import { XMarkIcon, MagnifyingGlassIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';

interface AssetBrowserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectAsset: (asset: AssetDto) => void;
}

const AssetBrowserModal: React.FC<AssetBrowserModalProps> = ({ isOpen, onClose, onSelectAsset }) => {
  const [assets, setAssets] = useState<AssetDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      const fetchAssets = async () => {
        setIsLoading(true);
        setError(null);
        try {
          const fetchedAssets = await assetsApi.getAll();
          // Now fetch preview URLs for each asset
          const assetsWithPreviews = await Promise.all(
            fetchedAssets.map(async (asset: AssetDto) => {
              try {
                const previewUrl = await assetsApi.getAssetPreviewUrl(asset.uuid);
                return { ...asset, previewUrl };
              } catch (e) {
                console.error(`Failed to get preview for ${asset.uuid}`, e);
                return { ...asset, previewUrl: '' }; // or a placeholder
              }
            })
          );
          setAssets(assetsWithPreviews);
        } catch (err) {
          setError('Failed to load assets.');
          console.error(err);
        } finally {
          setIsLoading(false);
        }
      };
      fetchAssets();
    }
  }, [isOpen]);

  const filteredAssets = assets.filter(asset =>
    asset.meta.filename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (asset: AssetDto) => {
    onSelectAsset(asset);
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Asset Browser</h2>
          <div className="flex items-center space-x-4">
            <button className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 flex items-center">
              <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
              Upload New Asset
            </button>
            <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-grow overflow-y-auto p-4">
          {isLoading && <p>Loading assets...</p>}
          {error && <p className="text-red-500">{error}</p>}
          {!isLoading && !error && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filteredAssets.map(asset => (
                <div
                  key={asset.uuid}
                  className="border rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleSelect(asset)}
                >
                  <div className="h-32 bg-gray-200 flex items-center justify-center">
                    {asset.previewUrl ? (
                      <img src={asset.previewUrl} alt={asset.meta.filename} className="h-full w-full object-cover" />
                    ) : (
                      <span className="text-gray-500 text-xs">No preview</span>
                    )}
                  </div>
                  <div className="p-2">
                    <p className="text-sm truncate" title={asset.meta.filename}>{asset.meta.filename}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!isLoading && filteredAssets.length === 0 && (
            <p className="text-center text-gray-500 mt-8">No assets found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssetBrowserModal;
